import { useState, useEffect, useCallback } from "react"
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { Plus, ChevronDown, ChevronUp, Camera, Lock } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import ScanQrStatusButton from '../shipment-qr-status/ScanQrStatusButton.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import { canViewMlSheet, canAppendShipmentsOnMlSheet, isAgentRole, isContractPartyAdminRole, showMlSheetListAmount, canDownloadMlSheetPdf, isAssignedMlAccessViewer } from '../../utils/mlSheetAgentAccess.js';
import { canCreateMlSheet, canCreateMlSheetByQr, isPickupMan } from '../../utils/networkMode.js';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass, listPageWrapperClass } from '../common/uiClasses.js';
import { canPrintShipmentQr, downloadShipmentQrCodesBulk } from '../../utils/shipmentQrPrint.js';
import { printPdfBlob } from '../../utils/printPdfBlob.js';


const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const MLSheetList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const pickupMan = isPickupMan(user)
  const [mlSheets, setMLSheets] = useState([])
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const today = getLocalDateString()
  const [filters, setFilters] = useState({
    from_location: "",
    to_location: "",
    vehicle_id: "",
    from_date: pickupMan ? today : "",
    to_date: pickupMan ? today : "",
    status: "",
    ml_sheet_id: "",
    shipment_id: "",
  })
  const [openMenuId, setOpenMenuId] = useState(null)
  const [expandedAccessId, setExpandedAccessId] = useState(null)
  const [pdfActionId, setPdfActionId] = useState(null)
  const [qrActionId, setQrActionId] = useState(null)
  const {
    loaded: mlBlockLoaded,
    mlCreationBlock,
    dues,
    hardBlocked: createHardBlocked,
    showGate: showPaidSettlementGate,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user)

  useEffect(() => {
    if (!pickupMan) return undefined
    setFilters((prev) => {
      if (prev.from_date || prev.to_date) return prev
      const day = getLocalDateString()
      return { ...prev, from_date: day, to_date: day }
    })
    return undefined
  }, [pickupMan])

  useEffect(() => {
    fetchLocations()
  }, [])

  const fetchMLSheets = useCallback(async () => {
    try {
      setLoading(true)
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ""))

      const response = await mlSheetService.getAllMLSheets(currentPage, 10, cleanFilters)
      
      const sheetsData = response.data.data || response.data.ml_sheets || []
      const rows = Array.isArray(sheetsData) ? sheetsData : []
      setMLSheets((prev) => mergeLoadedPage(currentPage, rows, prev))
      setTotalPages(response.data.pagination?.pages || 1)
      setTotalRecords(response.data.pagination?.total || rows.length)
    } catch (error) {
      console.error("Error fetching ML Sheets:", error)
      Alert.error(error.response?.data?.error || "Failed to load ML Sheets")
      if (currentPage <= 1) setMLSheets([])
    } finally {
      setLoading(false)
    }
  }, [filters, currentPage])

  usePaginatedListFetch({
    fetchPage: fetchMLSheets,
    setCurrentPage,
    currentPage,
    debounceMs: 0,
    resetPageDeps: [
      filters.from_location,
      filters.to_location,
      filters.vehicle_id,
      filters.from_date,
      filters.to_date,
      filters.status,
      filters.ml_sheet_id,
      filters.shipment_id,
    ],
  })

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
      if (expandedAccessId && !e.target.closest(".access-container")) {
        setExpandedAccessId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId, expandedAccessId])

  const fetchLocations = async () => {
    try {
      const response = await mlSheetService.getLocations()
      const locationsData = response.data.hubs || response.data.data || response.data || []
      setLocations(Array.isArray(locationsData) ? locationsData : [])
    } catch (error) {
      console.error("Error fetching locations:", error)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters({ ...filters, [name]: value })
  }

  const handleClearFilters = () => {
    setFilters({
      from_location: "",
      to_location: "",
      vehicle_id: "",
      from_date: pickupMan ? getLocalDateString() : "",
      to_date: pickupMan ? getLocalDateString() : "",
      status: "",
      ml_sheet_id: "",
      shipment_id: "",
    })
  }

  const hasActiveFilters = filters.ml_sheet_id || filters.shipment_id || filters.from_location || filters.to_location || filters.status || filters.from_date || filters.to_date

  const toggleMenu = (sheetKey) => {
    setOpenMenuId(openMenuId === sheetKey ? null : sheetKey)
  }

  const handleDelete = async (sheet) => {
    const mlSheetId = sheet.ml_sheet_id
    setOpenMenuId(null)
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete ML Sheet ${mlSheetId}? All shipments will be reverted to booked status.`,
      "Delete ML Sheet"
    )
    if (!confirmed) return

    try {
      await mlSheetService.deleteMLSheet(mlSheetId)
      Alert.success("ML Sheet deleted successfully")
      fetchMLSheets()
    } catch (error) {
      console.error("Error deleting ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to delete ML Sheet")
    }
  }

  const getStatusColor = (status) => {
    return status === "OPEN" || status === "open" 
      ? "border-green-500 bg-green-50 text-green-700" 
      : "border-red-500 bg-red-50 text-red-700"
  }

  const canEditSheet = (sheet) => {
    if (isAssignedMlAccessViewer(sheet, user)) return false
    if (sheet?.is_editable != null) return Boolean(sheet.is_editable)
    return user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  }

  const canDelete = () => {
    return user?.role === "System Admin" || user?.role === "Admin"
  }

  const canViewSheet = (sheet) => canViewMlSheet(sheet, user);

  const handleViewSheet = (sheet) => {
    if (!canViewSheet(sheet)) return
    navigate(`/ml-sheets/view/${sheet.ml_sheet_id}`)
  }

  const renderSheetIdLink = (sheet, className = listIdLinkClass) => {
    const label = `ML-${sheet.ml_sheet_id}`
    if (!canViewSheet(sheet)) {
      return <span className={className}>{label}</span>
    }
    return (
      <button
        type="button"
        onClick={() => handleViewSheet(sheet)}
        className={`text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded ${className}`}
        aria-label={`View ML Sheet ${label}`}
      >
        {label}
      </button>
    )
  }

  const handleDownloadPDF = async (sheet) => {
    const mlSheetId = sheet.ml_sheet_id
    setOpenMenuId(null)
    try {
      setPdfActionId(mlSheetId)
      const response = await mlSheetService.generatePDF(mlSheetId)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `ml-sheet-${mlSheetId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("PDF downloaded successfully")
    } catch (error) {
      console.error("Error downloading ML PDF:", error)
      Alert.error(error.response?.data?.error || "Failed to download PDF")
    } finally {
      setPdfActionId(null)
    }
  }

  const handlePrintPDF = async (sheet) => {
    const mlSheetId = sheet.ml_sheet_id
    setOpenMenuId(null)
    try {
      setPdfActionId(mlSheetId)
      const response = await mlSheetService.generatePDF(mlSheetId)
      await printPdfBlob(response.data, `ml-sheet-${mlSheetId}.pdf`)
    } catch (error) {
      console.error("Error printing ML PDF:", error)
      Alert.error(error.response?.data?.error || "Failed to print PDF")
    } finally {
      setPdfActionId(null)
    }
  }

  const handleDownloadShipmentQR = async (sheet) => {
    const mlSheetId = sheet.ml_sheet_id
    setOpenMenuId(null)
    try {
      setQrActionId(mlSheetId)
      await downloadShipmentQrCodesBulk(
        sheet.shipment_ids,
        `shipment_qr_codes_${mlSheetId}.pdf`,
      )
    } finally {
      setQrActionId(null)
    }
  }

  const buildActionExtraItems = (sheet) => {
    const pdfBusy = pdfActionId === sheet.ml_sheet_id
    const qrBusy = qrActionId === sheet.ml_sheet_id
    const items = []
    if (
      !isAssignedMlAccessViewer(sheet, user)
      && canAppendShipmentsOnMlSheet(sheet, user)
      && (user?.role !== 'Agent' || (mlBlockLoaded && !createHardBlocked))
    ) {
      items.push({
        label: 'Add Shipment',
        icon: '📦',
        onClick: () => navigate(`/ml-sheets/${sheet.ml_sheet_id}/add-shipment`),
      })
    }
    if (canDownloadMlSheetPdf(sheet, user)) {
      items.push(
        {
          label: pdfBusy ? 'Downloading...' : 'Download OGCL / OGPL',
          icon: '📄',
          onClick: () => handleDownloadPDF(sheet),
        },
        {
          label: pdfBusy ? t('common.loading') : 'Print OGCL / OGPL',
          icon: '🖨️',
          onClick: () => handlePrintPDF(sheet),
        },
      )
    }
    if (canPrintShipmentQr(user)) {
      items.push({
        label: qrBusy ? 'Downloading...' : 'Download Shipment QR',
        icon: '🔳',
        onClick: () => handleDownloadShipmentQR(sheet),
      })
    }
    return items
  }

  const renderSheetActions = (sheet) => {
    const pdfBusy = pdfActionId === sheet.ml_sheet_id
    const qrBusy = qrActionId === sheet.ml_sheet_id
    return (
      <RowActionsMenu
        className="relative menu-container"
        isOpen={openMenuId === sheet.ml_sheet_id}
        onToggle={() => toggleMenu(sheet.ml_sheet_id)}
        onClose={() => setOpenMenuId(null)}
        onView={canViewSheet(sheet) ? () => handleViewSheet(sheet) : undefined}
        onEdit={canEditSheet(sheet) ? () => navigate(`/ml-sheets/edit/${sheet.ml_sheet_id}`) : undefined}
        onDelete={canDelete() && !pdfBusy && !qrBusy ? () => handleDelete(sheet) : undefined}
        extraItems={buildActionExtraItems(sheet)}
      />
    )
  }

  const createActionsReady = mlBlockLoaded
  const showCreateActions = !pickupMan && (user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant" || user?.role === "Agent" || user?.role === "Hub Admin") && !isContractPartyAdminRole(user)
  const showAmountColumn = showMlSheetListAmount(user)
  const isAgentList = isAgentRole(user) && !pickupMan
  const showAccessUsersColumn = !isAgentList
  const showRemittanceColumn = isAgentList && showAmountColumn

  const formatSheetMoney = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`

  const formatSheetTotalAmount = (sheet) => formatSheetMoney(sheet.total_amount)

  const formatSheetRemittanceAmount = (sheet) =>
    formatSheetMoney(sheet.remittance_amount)

  const sheetShipmentCount = (sheet) =>
    Array.isArray(sheet.shipment_ids) ? sheet.shipment_ids.length : (sheet.shipment_count || 0)

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
                <ListPageHeader
                  title={t('pages.mlSheet.title')}
                  className="mb-4 sm:mb-6"
                >
                  {showCreateActions && (
                    <>
                      <ScanQrStatusButton mobileIconOnly />
                      {canCreateMlSheetByQr(user) && (
                      <Button mobileIconOnly
                        variant="secondary"
                        icon={Camera}
                        disabled={!createActionsReady}
                        onClick={() => createActionsReady && navigate("/ml-sheets/create-qr")}
                      >
                        QR Create
                      </Button>
                      )}
                      {canCreateMlSheet(user) && (
                      <Button mobileIconOnly
                        variant="primary"
                        icon={Plus}
                        disabled={!createActionsReady}
                        onClick={() => createActionsReady && navigate("/ml-sheets/add")}
                      >
                        Create Dispatch
                      </Button>
                      )}
                    </>
                  )}
                  {pickupMan && (
                    <ScanQrStatusButton mobileIconOnly />
                  )}
                </ListPageHeader>

                {mlBlockLoaded && (
                  <MlPaidSettlementGate
                    mlCreationBlock={mlCreationBlock}
                    dues={dues}
                    show={showPaidSettlementGate}
                    hardBlocked={createHardBlocked}
                    settleOpen={settleOpen}
                    setSettleOpen={setSettleOpen}
                    onSettleSubmitted={refreshMlSettlement}
                    variant="compact"
                    collapsible
                    defaultExpanded={false}
                    className="mb-4"
                  />
                )}

                <ListFilterPanel
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={handleClearFilters}
                  mobileSingleColumn
                >
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">ML Sheet ID</label>
                    <SearchSubmitInput
                      name="ml_sheet_id"
                      value={filters.ml_sheet_id}
                      onSearch={(value) => setFilters((prev) => ({ ...prev, ml_sheet_id: value }))}
                      placeholder="Search by ML sheet ID..."
                      inputClassName="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Shipment ID</label>
                    <SearchSubmitInput
                      name="shipment_id"
                      value={filters.shipment_id}
                      onSearch={(value) => setFilters((prev) => ({ ...prev, shipment_id: value }))}
                      placeholder="Search by shipment ID..."
                      inputClassName="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Location</label>
                    <select
                      name="from_location"
                      value={filters.from_location}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    >
                      <option value="">All Locations</option>
                      {locations.map((loc) => (
                        <option key={loc.id || loc.hub_id} value={loc.hub_name || loc.location || loc.name}>
                          {loc.hub_name || loc.location || loc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Location</label>
                    <select
                      name="to_location"
                      value={filters.to_location}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    >
                      <option value="">All Locations</option>
                      {locations.map((loc) => (
                        <option key={loc.id || loc.hub_id} value={loc.hub_name || loc.location || loc.name}>
                          {loc.hub_name || loc.location || loc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    >
                      <option value="">All Status</option>
                      <option value="OPEN">Open</option>
                      <option value="CLOSE">Close</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
                    <input
                      type="date"
                      name="from_date"
                      value={filters.from_date}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
                    <input
                      type="date"
                      name="to_date"
                      value={filters.to_date}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                </ListFilterPanel>

                {/* Table & Cards */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden w-full min-w-0 max-w-full">
                  {loading && currentPage === 1 ? (
                    <div className="py-16">
                      <Loader fullScreen={false} size="default" message="Loading Dispatch..." />
                    </div>
                  ) : mlSheets.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg font-semibold">No Dispatch records found</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop table from lg up so header + columns stay aligned */}
                      <div className="hidden lg:block w-full min-w-0 overflow-x-auto">
                        <table className="w-full min-w-[880px] table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                          <thead>
                            <tr className="bg-gray-50 text-gray-700 border-b">
                              <th className="w-[6%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Sr No</th>
                              <th className="w-[14%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Dispatch No</th>
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Created</th>
                              {showAmountColumn && (
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Total Amount</th>
                              )}
                              {showRemittanceColumn && (
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Remittance</th>
                              )}
                              <th className="w-[22%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">From → To</th>
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Vehicle</th>
                              <th className="w-[8%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">{t('pages.shipment.title')}</th>
                              {showAccessUsersColumn && (
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Access Users</th>
                              )}
                              <th className="w-[8%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">Status</th>
                              <th className="w-[4%] px-3 2xl:px-4 py-3 text-xs lg:text-sm font-semibold text-center whitespace-normal break-words">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {mlSheets.map(
                              (sheet, index) => (
                                  <tr
                                    key={sheet.id}
                                    className={`hover:bg-gray-50 text-center transition-colors relative ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                                  >
                                    <td className="px-3 2xl:px-4 py-3 text-sm font-regular align-top whitespace-normal break-words">
                                      {index + 1}
                                    </td>
                                    <td className="px-3 2xl:px-4 py-3 text-center align-top whitespace-normal break-all">
                                      {renderSheetIdLink(sheet, listIdLinkClass)}
                                    </td>
                                    <td className="px-3 2xl:px-4 py-3 text-sm text-gray-600 text-center align-top whitespace-normal break-words">
                                      {new Date(sheet.created_at).toLocaleDateString("en-IN")}
                                    </td>
                                    {showAmountColumn && (
                                    <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top whitespace-normal break-words">
                                      <span className="font-regular text-gray-900">
                                        {formatSheetTotalAmount(sheet)}
                                      </span>
                                    </td>
                                    )}
                                    {showRemittanceColumn && (
                                    <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top whitespace-normal break-words">
                                      <span className="font-regular text-gray-900">
                                        {formatSheetRemittanceAmount(sheet)}
                                      </span>
                                    </td>
                                    )}
                                    <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top">
                                      <div className="font-semiboldregular text-gray-700 whitespace-normal break-words leading-snug">
                                        <span>
                                          {sheet.from_locations && sheet.from_locations.length > 0
                                            ? sheet.from_locations
                                                .map(loc => loc.location || loc.hub_name || loc.name)
                                                .join(", ")
                                            : sheet.from_location?.location || sheet.from_location || "N/A"}
                                        </span>
                                        {" "}
                                        <span className="text-secondary">→</span>
                                        {" "}
                                        <span>
                                          {sheet.to_locations && sheet.to_locations.length > 0
                                            ? sheet.to_locations
                                                .map(loc => loc.location || loc.hub_name || loc.name)
                                                .join(", ")
                                            : sheet.to_location?.location || sheet.to_location || "N/A"}
                                        </span>
                                      </div>
                                    </td>

                                    <td className="px-3 2xl:px-4 py-3 text-center align-top">
                                      <div className="font-regular text-gray-700 text-sm whitespace-normal break-words">{sheet.vehicle_number}</div>
                                      {/*<div className="text-sm text-secondary">{sheet.driver_contact}</div>*/}
                                    </td>
                                    <td className="px-3 2xl:px-4 py-3 text-center align-top">
                                      <span className="inline-block px-3 py-1 text-gray-700 font-regular text-sm">
                                        {sheetShipmentCount(sheet)}
                                      </span>
                                    </td>
                                    {showAccessUsersColumn && (
                                    <td className="px-3 2xl:px-4 py-3 text-center align-top">
                                      <div className="relative access-container">
                                        <button
                                          onClick={() =>
                                            setExpandedAccessId(expandedAccessId === sheet.id ? null : sheet.id)
                                          }
                                          className="inline-flex items-center justify-center gap-1 text-gray-700 font-regular text-sm whitespace-normal break-words"
                                        >
                                          {expandedAccessId === sheet.id ? (
                                            <ChevronUp size={16} />
                                          ) : (
                                            <ChevronDown size={16} />
                                          )}
                                          {sheet.access_users?.length || 0} Users
                                        </button>
                                        {expandedAccessId === sheet.id && (
                                          <div className="absolute top-full mt-1 left-0 bg-white border border-blue-300 rounded-xl shadow-lg z-10 min-w-48">
                                            {sheet.access_users?.map((accessUser) => {
                                              const userId = accessUser.user_id || accessUser
                                              const userName = accessUser.name || `User ${userId}`
                                              const userLocation = accessUser.location || ''
                                              return (
                                                <button
                                                  key={userId}
                                                  onClick={() => navigate(`/users/view/${userId}`)}
                                                  className="block w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-200 font-semibold text-gray-800"
                                                >
                                                  <div className="font-semibold">{userName} - {userLocation}</div>
                                                </button>
                                              )
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    )}
                                    <td className="px-3 2xl:px-4 py-3 text-center align-top">
                                      <span className="inline-flex flex-wrap items-center justify-center gap-1 px-1 py-1 text-sm font-regular uppercase text-gray-700 whitespace-normal break-words">
                                        {sheet.status}
                                        {sheet.is_editable === false && (
                                          <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" title={sheet.edit_blockers?.[0]?.message || 'Locked'} />
                                        )}
                                        {sheet.can_update_status && user?.role === 'Agent' && (
                                          <span className="text-[10px] normal-case text-green-600 font-semibold">QR</span>
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top">
                                      {renderSheetActions(sheet)}
                                    </td>
                                  </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile & tablet cards */}
                      <div className="lg:hidden space-y-3 p-4">
                        {mlSheets.map(
                          (sheet) => (
                              <div key={sheet.id} className="bg-white border rounded-xl p-4 shadow-sm">
                                <div className="mb-3">
                                  <div className="flex justify-between items-start gap-2">
                                    <div className="min-w-0 flex-1">
                                      {renderSheetIdLink(
                                        sheet,
                                        `${listIdLinkMobileClass} w-full block`
                                      )}
                                      <div className="text-sm text-gray-600 mt-1 break-words">
                                        {sheet.from_locations && sheet.from_locations.length > 0 
                                          ? sheet.from_locations.map(loc => loc.location || loc.hub_name || loc.name).join(', ')
                                          : sheet.from_location?.location || sheet.from_location || 'N/A'} 
                                        → 
                                        {sheet.to_locations && sheet.to_locations.length > 0 
                                          ? sheet.to_locations.map(loc => loc.location || loc.hub_name || loc.name).join(', ')
                                          : sheet.to_location?.location || sheet.to_location || 'N/A'}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span
                                        className={`px-2 py-1 rounded text-xs font-bold border whitespace-nowrap ${getStatusColor(sheet.status)}`}
                                      >
                                        {sheet.status}
                                      </span>
                                      {renderSheetActions(sheet)}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                                  <div>
                                    <span className="text-gray-600">Created:</span>
                                    <div className="font-semibold">{new Date(sheet.created_at).toLocaleDateString("en-IN")}</div>
                                  </div>
                                  {showAmountColumn && (
                                  <div>
                                    <span className="text-gray-600">Total Amount:</span>
                                    <div className="font-semibold">
                                      {formatSheetTotalAmount(sheet)}
                                    </div>
                                  </div>
                                  )}
                                  {showRemittanceColumn && (
                                  <div>
                                    <span className="text-gray-600">Remittance:</span>
                                    <div className="font-semibold">
                                      {formatSheetRemittanceAmount(sheet)}
                                    </div>
                                  </div>
                                  )}
                                  <div>
                                    <span className="text-gray-600">Vehicle:</span>
                                    <div className="font-semibold">{sheet.vehicle_number}</div>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Shipments:</span>
                                    <div className="font-semibold">{sheetShipmentCount(sheet)}</div>
                                  </div>
                                </div>
                              </div>
                            )
                        )}
                      </div>

                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading}
                        onNext={() => setCurrentPage((page) => page + 1)}
                      />
                    </>
                  )}
                </div>
      </div>
    </DashboardLayout>
  )
}

export default MLSheetList;
